import { useQuery } from "@apollo/client";
import moment from "moment";
import { useState } from "react";
import {
  GetRelatedRecognitionForCoreValueQuestionQuery,
  GetRelatedRecognitionForCoreValueQuestionQueryVariables,
} from "types/graphql-schema";

import getRelatedRecognitionForCoreValueQuestionQuery from "@apps/assessments/graphql/get-related-recognition-for-core-value-question-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Loading from "@components/loading/loading";
import MotionDiv from "@components/motion/motion-div";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
} from "@components/toggle-button-group/toggle-button-group";
import { assertNonNull } from "@helpers/helpers";

enum RelatedRecognitionDisplay {
  sent,
  received,
}

const CoreValuesQuestionRelatedRecognition = ({
  coreValueId,
  targetId,
  periodStartDate,
  periodEndDate,
}: {
  coreValueId: number;
  targetId: number;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const [recognitionDisplay, setRecognitionDisplay] =
    useState<RelatedRecognitionDisplay>(RelatedRecognitionDisplay.received);

  const { data, loading: isLoading } = useQuery<
    GetRelatedRecognitionForCoreValueQuestionQuery,
    GetRelatedRecognitionForCoreValueQuestionQueryVariables
  >(getRelatedRecognitionForCoreValueQuestionQuery, {
    variables: {
      organizationId: organization.id,
      coreValueId,
      recipientId:
        recognitionDisplay === RelatedRecognitionDisplay.received
          ? targetId
          : null,
      createdBy:
        recognitionDisplay === RelatedRecognitionDisplay.sent ? targetId : null,
      createdBetweenDates:
        periodStartDate && periodEndDate
          ? [
              periodStartDate,
              moment(periodEndDate).add(1, "day").format("YYYY-MM-DD"),
            ]
          : [
              moment().subtract(1, "year").format("YYYY-MM-DD"),
              moment().add(1, "day").format("YYYY-MM-DD"),
            ],
    },
  });

  return (
    <div>
      <div className="flex items-center gap-6">
        <div className="text-sm font-semibold">
          Related {label("recognition", { pluralize: true, capitalize: true })}
        </div>
        <ToggleButtonGroup<RelatedRecognitionDisplay>
          theme={ToggleButtonGroupTheme.roundedCompact}
          buttons={[
            {
              title: "Received",
              active: recognitionDisplay === RelatedRecognitionDisplay.received,
              value: RelatedRecognitionDisplay.received,
            },
            {
              title: "Sent",
              active: recognitionDisplay === RelatedRecognitionDisplay.sent,
              value: RelatedRecognitionDisplay.sent,
            },
          ]}
          onClick={(buttonGroup) =>
            setRecognitionDisplay(assertNonNull(buttonGroup.value))
          }
        />
      </div>

      <div className="text-sm mt-4">
        {isLoading && (
          <div className="flex justify-center">
            <Loading />
          </div>
        )}
        {!isLoading && data?.artifacts?.edges.length === 0 && (
          <div className="flex justify-center text-gray-400">
            No {label("recognition", { pluralize: true })} to show
          </div>
        )}

        {!isLoading &&
          data?.artifacts?.edges &&
          data?.artifacts?.edges.length > 0 && (
            <div className="flex flex-col gap-6 text-sm">
              {data?.artifacts?.edges.map((edge) => {
                const artifact = edge?.node;
                if (
                  !artifact ||
                  artifact.__typename !== "RecognitionArtifactNode"
                ) {
                  return null;
                }
                return (
                  <MotionDiv
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    key={artifact.id}
                    className="flex items-start text-sm"
                  >
                    <div className="flex items-center">
                      <div className="shrink-0 text-gray-600 mr-2">
                        {recognitionDisplay ===
                        RelatedRecognitionDisplay.received
                          ? "From"
                          : "To"}
                        :
                      </div>
                      <div className="w-36 mr-4 flex items-center">
                        <Avatar
                          className="mr-2"
                          user={artifact.creator}
                          size={4}
                        />
                        <div className="text-ellipsis text-gray-600 font-medium overflow-hidden text-nowrap">
                          {artifact.creator?.name}
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">{artifact.title}</div>
                  </MotionDiv>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
};

export default CoreValuesQuestionRelatedRecognition;
